import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import PortalHeader from '../components/PortalHeader';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import {
  loadClubUplinkManifests,
  loadAllUplinkManifests,
  formatDate,
  formatBytes,
  formatUtcSpan,
  formatAgo,
  positionFixCount,
  hasPositionData,
  sentenceTypeRows,
  isLive,
  LIVE_WINDOW_MS,
} from '../lib/uplinkStore';

// How often to re-read the manifests while at least one vessel is still
// uplinking. The manifest is bumped per worker flush, so this is as live as the
// pointer docs get — and when nothing is live there is nothing to poll for, so
// an idle club-day costs one read, not one every half minute.
const LIVE_POLL_MS = 30 * 1000;

/** Compact "RMC 9,012 · MWV 4,501 · +3 more" summary of a sentence histogram. */
function SentenceSummary({ sentenceTypes }) {
  const rows = sentenceTypeRows(sentenceTypes);
  if (rows.length === 0) return <span className="muted">—</span>;
  const shown = rows.slice(0, 3);
  const rest = rows.length - shown.length;
  return (
    <span title={rows.map(([t, n]) => `${t} ${n.toLocaleString()}`).join('\n')}>
      {shown.map(([t, n]) => `${t} ${n.toLocaleString()}`).join(' · ')}
      {rest > 0 && ` · +${rest} more`}
    </span>
  );
}

export default function OnboardUplink() {
  const { clubId } = useParams();
  // Claim-backed, NOT plain isGlobalAdmin: firestore.rules only permits the
  // unconstrained list to a portalRole=='GlobalAdmin' CLAIM. An admin-domain
  // user is a global admin for routing but is refused by the rule, so offering
  // them the toggle would be offering an action that always fails.
  const { isClaimGlobalAdmin } = useAuth();

  const [rows, setRows] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [allClubs, setAllClubs] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Monotonic request id. A poll and a scope change (club → all clubs) can be
  // in flight together, and a slow earlier read landing last would show the
  // wrong club's boats — so only the newest request is allowed to write state.
  const reqRef = useRef(0);

  // A poll must not blank the table or re-show "Loading…" — an admin watching a
  // boat come in would get a flicker every 30s. Only the first load for a given
  // scope clears what is on screen.
  const load = useCallback(async ({ quiet }) => {
    const id = reqRef.current + 1;
    reqRef.current = id;
    if (!quiet) {
      setRows(null);
      setError(null);
    }
    try {
      const next = allClubs && isClaimGlobalAdmin
        ? await loadAllUplinkManifests()
        : await loadClubUplinkManifests(clubId);
      if (reqRef.current !== id) return; // superseded
      setRows(next);
      setError(null);
      setNow(Date.now());
    } catch (e) {
      if (reqRef.current !== id) return;
      // A failed refresh must not destroy a table that is already useful:
      // surface the error and keep the last good rows when we have them.
      setError(e.message || String(e));
      if (!quiet) setRows([]);
    }
  }, [clubId, allClubs, isClaimGlobalAdmin]);

  useEffect(() => { load({ quiet: false }); }, [load]);

  // Poll only while something is actually live. `anyLive` is recomputed from
  // the rows we last read, so a boat going quiet stops the polling by itself.
  // The interval reads `load` through a ref so a scope change doesn't tear down
  // and restart the timer (which would reset the 30s cadence on every render).
  const anyLive = (rows || []).some((r) => isLive(r, now));
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);
  useEffect(() => {
    if (!anyLive) return undefined;
    const t = setInterval(() => { loadRef.current({ quiet: true }); }, LIVE_POLL_MS);
    return () => clearInterval(t);
  }, [anyLive]);

  const liveMinutes = Math.round(LIVE_WINDOW_MS / 60000);
  const showClubColumn = allClubs && isClaimGlobalAdmin;

  return (
    <>
      <PortalHeader />
      <div className="page">
        <PageHeader
          backTo={`/clubs/${clubId}`}
          backLabel="Dashboard"
          title="Onboard Uplink"
        />

        <p className="notice">
          Boats running Racemarks Onboard push their instrument bus to the
          uplink, which files one record per boat per day. This is that record:
          what has arrived, from which boat, over what span, and whether it
          contains position fixes. Times are UTC, matching the day each record
          is filed under.
        </p>

        {isClaimGlobalAdmin && (
          <p className="filter-row">
            <label>
              <input
                type="checkbox"
                checked={allClubs}
                onChange={(e) => setAllClubs(e.target.checked)}
              />
              {' '}Show every club
            </label>
            {' '}
            <span className="muted">
              Club keys have changed over time, so some older records are filed
              under a different club and won&rsquo;t appear in {clubId}.
            </span>
          </p>
        )}

        {error && (
          <div className="error">
            {/^(permission|missing or insufficient)/i.test(error)
              ? <>Your account can&rsquo;t read this club&rsquo;s uplink records.
                  Access comes from your portal role, which is carried in your
                  sign-in token — so if your role was granted recently, sign out
                  and back in to pick it up. Otherwise ask a global admin for a
                  ClubAdmin role for {clubId}.</>
              : <>Couldn&rsquo;t load uplink records: {error}</>}
          </div>
        )}

        {rows === null && !error && <p className="empty-state">Loading uplink records…</p>}

        {rows !== null && rows.length === 0 && !error && (
          <p className="empty-state">
            No uplink records for {showClubColumn ? 'any club' : clubId} yet. A
            boat appears here the first time its Onboard uplink flushes.
          </p>
        )}

        {rows !== null && rows.length > 0 && (
          <>
            <table className="config-table">
              <thead>
                <tr>
                  <th>Date</th>
                  {showClubColumn && <th>Club</th>}
                  <th>Boat</th>
                  <th>Span (UTC)</th>
                  <th className="uplink-num">Parts</th>
                  <th className="uplink-num">Size</th>
                  <th className="uplink-num">Positions</th>
                  <th>Sentences</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const live = isLive(r, now);
                  const ago = formatAgo(r.lastMs, now);
                  const fixes = positionFixCount(r.sentenceTypes);
                  return (
                    <tr key={r.id}>
                      <td>{formatDate(r.date)}</td>
                      {showClubColumn && <td><code>{r.club || '—'}</code></td>}
                      <td>
                        <strong>{r.vessel || '—'}</strong>
                        {live && (
                          <span
                            className="maturity-badge live-badge"
                            data-testid="live-badge"
                            title={`Last flush ${ago} (within ${liveMinutes} min)`}
                          >
                            live
                          </span>
                        )}
                      </td>
                      <td>
                        {formatUtcSpan(r.firstMs, r.lastMs) || <span className="muted">—</span>}
                        {!live && ago && (
                          <> <span className="muted">({ago})</span></>
                        )}
                      </td>
                      <td className="uplink-num">
                        {r.partCount === null
                          ? <span className="muted">—</span>
                          : r.partCount.toLocaleString()}
                      </td>
                      <td className="uplink-num">{formatBytes(r.bytes) || <span className="muted">—</span>}</td>
                      <td className="uplink-num">
                        {fixes === null
                          ? <span className="muted">unknown</span>
                          : hasPositionData(r.sentenceTypes)
                            ? fixes.toLocaleString()
                            : <span className="muted">none</span>}
                      </td>
                      <td><SentenceSummary sentenceTypes={r.sentenceTypes} /></td>
                      <td>{r.source ? <code>{r.source}</code> : <span className="muted">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <p className="notice">
              <strong>Positions</strong> counts the position-bearing sentences
              (RMC, GLL, GGA, GNS) in the day&rsquo;s stream — a boat sending
              only wind and depth is uplinking fine but has no track to draw.
              {' '}
              <strong>Source</strong> is whichever feed flushed most recently,
              not the full list: a boat carrying two uplink tokens (say an
              instrument feed and an AIS feed) files both under this same record,
              so only the last one to flush is named here.
              {' '}
              <strong>live</strong> means a flush landed within the last{' '}
              {liveMinutes} minutes; the table refreshes itself while any boat is
              live.
            </p>
          </>
        )}
      </div>
    </>
  );
}
